import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import InputField from '../../components/InputField';
import TextAreaField from '../../components/TextAreaField';
import PhoneInputField from '../../components/PhoneInputField';
import FileUpload from '../../components/FileUpload/FileUpload';
import OptionSelector from '../../components/OptionSelector';
import PageHeader from '../../components/PageHeader/PageHeader';
import StepContent from '../../components/StepContent/StepContent';
import NavigationButtons from '../../components/NavigationButtons/NavigationButtons';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import useHandleErrorFields from '../../hooks/useHandleErrorFields';
import useBrandRegistrationService from '../../app/services/hooks/useBrandRegistrationService';
import useServicePricingService, { ServicePricingPlan } from '../../app/services/hooks/useServicePricingService';
import { BrandRegistrationDataForm, defaultBrandRegistrationDataForm } from '../../app/models/BrandRegistration';
import { toast } from 'react-toastify';
import '../../styles/page-components.css';

// Datos dummy para servicios
const servicesOptions = [
    { value: 'electrodomesticos', label: 'Electrodomésticos' },
    { value: 'linea_blanca', label: 'Línea blanca' },
    { value: 'tecnologia', label: 'Tecnología' },
    { value: 'ropa', label: 'Ropa y accesorios' },
    { value: 'calzado', label: 'Calzado' },
    { value: 'deportes', label: 'Artículos deportivos' },
    { value: 'hogar', label: 'Artículos para el hogar' },
    { value: 'jardineria', label: 'Jardinería' },
    { value: 'automotriz', label: 'Automotriz' },
    { value: 'salud', label: 'Salud y belleza' },
    { value: 'alimentacion', label: 'Alimentación' },
    { value: 'bebidas', label: 'Bebidas' },
    { value: 'servicios_profesionales', label: 'Servicios profesionales' },
    { value: 'consultoria', label: 'Consultoría' },
    { value: 'educacion', label: 'Educación' },
    { value: 'entretenimiento', label: 'Entretenimiento' },
    { value: 'turismo', label: 'Turismo y viajes' },
    { value: 'inmobiliaria', label: 'Inmobiliaria' },
    { value: 'financiero', label: 'Servicios financieros' },
    { value: 'logistica', label: 'Logística y transporte' }
];

const BrandRegistrationApplication = () => {
    console.log('BrandRegistrationApplication renderizado');
    const history = useHistory();
    const [currentStep, setCurrentStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { requestConfirmation, successAlert, errorAlert, infoAlert } = useReactConfirmAlert();
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();
    const { storeBrandRegistration, fetchingStoreBrandRegistration } = useBrandRegistrationService();
    const { getVerificationPlans, fetchingPlans } = useServicePricingService();

    const [formData, setFormData] = useState<BrandRegistrationDataForm>(defaultBrandRegistrationDataForm);

    // Desde la cartera del estudio se puede abrir con los datos del cliente ya cargados
    const location = useLocation<{ prefill?: Partial<Record<string, string>> }>();
    useEffect(() => {
        const source = location.state?.prefill || {};
        const prefill: Partial<BrandRegistrationDataForm> = {};
        (['applicant_email', 'applicant_first_name', 'applicant_paternal_last_name', 'applicant_maternal_last_name'] as const)
            .forEach((field) => {
                const value = source[field];
                if (value) (prefill as any)[field] = value;
            });
        if (Object.keys(prefill).length) setFormData((prev) => ({ ...prev, ...prefill }));
    }, []);
    
    // Planes de precio
    const [pricingPlans, setPricingPlans] = useState<ServicePricingPlan[]>([]);
    const [defaultPlanId, setDefaultPlanId] = useState<number | null>(null);
    
    // Cargar planes de precio al montar el componente
    useEffect(() => {
        getVerificationPlans({
            onSuccess: (response) => {
                const plans = response.data.plans || [];
                setPricingPlans(plans);
                const defaultId = response.data.default_plan_id;
                setDefaultPlanId(defaultId);
                // Seleccionar el plan por defecto automáticamente
                if (defaultId && !formData.selected_pricing_id) {
                    setFormData(prev => ({
                        ...prev,
                        selected_pricing_id: defaultId
                    }));
                }
            }
        });
    }, []);

    const handleInputChange = (field: keyof BrandRegistrationDataForm, value: string | File | null) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Paso 1: Validar plan de servicio
    const validatePlanStep = (): boolean => {
        const validationErrors: { [key: string]: string[] } = {};

        if (!formData.selected_pricing_id) {
            validationErrors.selected_pricing_id = ['Debe seleccionar un plan de servicio'];
        }

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            toast.error('Por favor seleccione un plan de servicio');
            return false;
        }

        // Limpiar errores si la validación pasa
        setErrors({});
        return true;
    };

    // Paso 2: Validar datos del solicitante
    const validateApplicantStep = (): boolean => {
        const validationErrors: { [key: string]: string[] } = {};

        if (!formData.applicant_first_name || formData.applicant_first_name.trim() === '') {
            validationErrors.applicant_first_name = ['El nombre es requerido'];
        }

        if (!formData.applicant_paternal_last_name || formData.applicant_paternal_last_name.trim() === '') {
            validationErrors.applicant_paternal_last_name = ['El apellido paterno es requerido'];
        }

        if (!formData.applicant_maternal_last_name || formData.applicant_maternal_last_name.trim() === '') {
            validationErrors.applicant_maternal_last_name = ['El apellido materno es requerido'];
        }

        if (!formData.applicant_email || formData.applicant_email.trim() === '') {
            validationErrors.applicant_email = ['El correo electrónico es requerido'];
        } else {
            // Validar formato de email
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.applicant_email)) {
                validationErrors.applicant_email = ['El correo electrónico no tiene un formato válido'];
            }
        }

        if (!formData.applicant_phone || formData.applicant_phone.trim() === '') {
            validationErrors.applicant_phone = ['El teléfono es requerido'];
        } else {
            // Validar que tenga al menos el código de país y algunos dígitos
            const phoneRegex = /^\+\d{1,4}\s?\d{6,}$/;
            if (!phoneRegex.test(formData.applicant_phone.replace(/\s/g, ''))) {
                validationErrors.applicant_phone = ['El teléfono debe incluir el código de país y al menos 6 dígitos'];
            }
        }

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            toast.error('Por favor complete todos los campos correctamente');
            return false;
        }

        // Limpiar errores si la validación pasa
        setErrors({});
        return true;
    };

    // Paso 3: Validar datos de la marca
    const validateBrandStep = (): boolean => {
        const validationErrors: { [key: string]: string[] } = {};

        if (!formData.mark_type) {
            validationErrors.mark_type = ['Debe seleccionar un tipo de marca'];
        }

        if (!formData.services_description || formData.services_description.trim() === '') {
            validationErrors.services_description = ['La descripción de servicios es requerida'];
        }

        // Validaciones según tipo de marca
        if (formData.mark_type === 'denominativa' || formData.mark_type === 'mixta') {
            if (!formData.brand_name || formData.brand_name.trim() === '') {
                validationErrors.brand_name = ['El nombre de la marca es requerido'];
            }
        }

        if (formData.mark_type === 'denominativa') {
            if (!formData.keywords || formData.keywords.trim() === '') {
                validationErrors.keywords = ['Las palabras clave son requeridas para marca denominativa'];
            }
        }

        if (formData.mark_type === 'figurativa' || formData.mark_type === 'mixta') {
            if (!formData.brand_image) {
                validationErrors.brand_image = ['La imagen de la marca es requerida'];
            }
        }

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            toast.error('Por favor complete todos los campos correctamente');
            return false;
        }

        // Limpiar errores si la validación pasa
        setErrors({});
        return true;
    };

    const handleNext = () => {
        if (currentStep === 1) {
            // Paso 1: Validar plan de servicio
            if (!validatePlanStep()) {
                return;
            }
            setErrors({});
            setCurrentStep(2);
        } else if (currentStep === 2) {
            // Paso 2: Validar datos del solicitante
            if (!validateApplicantStep()) {
                return;
            }
            setErrors({});
            setCurrentStep(3);
        } else if (currentStep === 3) {
            // Paso 3: Validar datos de la marca antes de enviar
            if (!validateBrandStep()) {
                return;
            }
            // Mostrar modal de confirmación
            requestConfirmation({
                title: '¿Confirmar envío?',
                message: '¿Está seguro de que desea enviar la solicitud de registro de marca?',
                buttons: {
                    confirmButton: {
                        label: 'Sí, enviar',
                        onClick: handleConfirmSubmit
                    },
                    cancelButton: {
                        label: 'Cancelar',
                        onClick: () => {
                            // No hacer nada, solo cerrar el modal
                        }
                    }
                }
            });
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            // Limpiar errores al volver al paso anterior
            setErrors({});
            setCurrentStep(currentStep - 1);
        } else {
            history.push('/intranet/brand-registration');
        }
    };

    const handleConfirmSubmit = async () => {
        setIsSubmitting(true);
        
        // Preparar datos para envío, removiendo campos no necesarios según el tipo de marca
        const dataToSend = { ...formData };
        
        // Si es marca denominativa, no enviar brand_image
        if (formData.mark_type === 'denominativa') {
            delete dataToSend.brand_image;
        }
        
        // Si es marca figurativa, no enviar brand_name ni keywords
        if (formData.mark_type === 'figurativa') {
            delete dataToSend.brand_name;
            delete dataToSend.keywords;
        }
        
        console.log('Datos del formulario:', dataToSend);
        storeBrandRegistration(dataToSend, {
            onSuccess: (response) => {
                setIsSubmitting(false);
                setErrors({}); // Limpiar errores
                successAlert({
                    title: '¡Solicitud enviada!',
                    message: 'Su solicitud de registro de marca ha sido enviada correctamente. Recibirá una notificación por email con los próximos pasos.'
                });
                // Redirigir después de un breve delay para que el usuario vea el mensaje
                setTimeout(() => {
                    history.push('/intranet/brand-registration');
                }, 2000);
            },
            onError: (response) => {
                setIsSubmitting(false);
                errorAlert({
                    title: 'Error al enviar',
                    message: response.message || 'Hubo un problema al enviar su solicitud. Por favor, intente nuevamente.'
                });
            },
            onFieldError: (response) => {
                setIsSubmitting(false);
                // Establecer errores del backend
                setErrors(response.data || {});
            }
        });
    };


    const renderStepContent = () => {
        switch (currentStep) {
            // PASO 1: Plan de servicio (primero)
            case 1:
                return (
                    <StepContent
                        title="Plan de servicio"
                        currentStep={1}
                        totalSteps={3}
                        progress={33}
                        infoTooltip={{
                            title: 'Plan de Servicio',
                            message: 'Selecciona el plan de servicio que mejor se adapte a tus necesidades. Este precio corresponde al servicio de registro de marca completo, incluyendo el análisis de factibilidad, seguimiento del proceso y asesoría legal.'
                        }}
                    >
                        <div className="form-fields">
                            <div className="form-section">
                                <h3 className="section-title">
                                    Selecciona tu plan de servicio:
                                </h3>
                                <p className="text-muted mb-4" style={{ fontSize: '0.95rem' }}>
                                    Este valor corresponde al servicio de registro de marca. Las tasas de INAPI se calcularán posteriormente según las clases que se seleccionen.
                                </p>
                                
                                {fetchingPlans ? (
                                    <div className="text-center py-4">
                                        <i className="fas fa-spinner fa-spin me-2"></i>
                                        Cargando planes disponibles...
                                    </div>
                                ) : pricingPlans.length === 0 ? (
                                    <div className="alert alert-warning">
                                        <i className="fas fa-exclamation-triangle me-2"></i>
                                        No hay planes de precio configurados. Por favor, contacte al administrador.
                                    </div>
                                ) : (
                                    <div className="row g-3 mb-4">
                                        {pricingPlans.map((plan) => (
                                            <div key={plan.id} className="col-md-4">
                                                <div 
                                                    className={`card h-100 cursor-pointer transition-all ${formData.selected_pricing_id === plan.id ? 'border-primary border-2 shadow' : 'border'}`}
                                                    onClick={() => {
                                                        setFormData(prev => ({ ...prev, selected_pricing_id: plan.id }));
                                                        onFocusRemove('selected_pricing_id');
                                                    }}
                                                    style={{ 
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s ease',
                                                        transform: formData.selected_pricing_id === plan.id ? 'scale(1.02)' : 'scale(1)'
                                                    }}
                                                >
                                                    {plan.is_default && (
                                                        <div 
                                                            className="position-absolute top-0 end-0 m-2"
                                                            style={{ zIndex: 1 }}
                                                        >
                                                            <span className="badge bg-primary" style={{ fontSize: '0.7rem' }}>
                                                                Recomendado
                                                            </span>
                                                        </div>
                                                    )}
                                                    <div className="card-body text-center">
                                                        <div className="mb-2">
                                                            <input
                                                                type="radio"
                                                                name="selectedPlan"
                                                                checked={formData.selected_pricing_id === plan.id}
                                                                onChange={() => {
                                                                    setFormData(prev => ({ ...prev, selected_pricing_id: plan.id }));
                                                                    onFocusRemove('selected_pricing_id');
                                                                }}
                                                                style={{ 
                                                                    width: '1.25rem', 
                                                                    height: '1.25rem',
                                                                    accentColor: '#001849'
                                                                }}
                                                            />
                                                        </div>
                                                        <h6 className="card-title mb-2" style={{ fontWeight: 600, color: '#001849' }}>
                                                            {plan.plan_name}
                                                        </h6>
                                                        <div className="mb-3">
                                                            <span style={{ fontSize: '1.75rem', fontWeight: 700, color: '#001849' }}>
                                                                ${plan.base_price.toLocaleString('es-CL')}
                                                            </span>
                                                            <span className="text-muted ms-1" style={{ fontSize: '0.9rem' }}>
                                                                {plan.currency}
                                                            </span>
                                                        </div>
                                                        {plan.description && (
                                                            <p className="text-muted small mb-3" style={{ fontSize: '0.85rem' }}>
                                                                {plan.description}
                                                            </p>
                                                        )}
                                                        {plan.features && plan.features.length > 0 && (
                                                            <ul className="list-unstyled text-start small mb-0">
                                                                {plan.features.slice(0, 4).map((feature, idx) => (
                                                                    <li key={idx} className="mb-1">
                                                                        <i className="fas fa-check text-success me-2" style={{ fontSize: '0.75rem' }}></i>
                                                                        <span style={{ fontSize: '0.8rem', color: '#495057' }}>{feature}</span>
                                                                    </li>
                                                                ))}
                                                                {plan.features.length > 4 && (
                                                                    <li className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                                        +{plan.features.length - 4} más...
                                                                    </li>
                                                                )}
                                                            </ul>
                                                        )}
                                                        {plan.exclusions && plan.exclusions.length > 0 && (
                                                            <ul className="list-unstyled text-start small mb-0 mt-2 pt-2" style={{ borderTop: '1px dashed #e0e0e0' }}>
                                                                {plan.exclusions.slice(0, 3).map((exclusion, idx) => (
                                                                    <li key={idx} className="mb-1">
                                                                        <i className="fas fa-times text-danger me-2" style={{ fontSize: '0.75rem' }}></i>
                                                                        <span style={{ fontSize: '0.8rem', color: '#dc3545' }}>{exclusion}</span>
                                                                    </li>
                                                                ))}
                                                                {plan.exclusions.length > 3 && (
                                                                    <li className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                                        +{plan.exclusions.length - 3} más...
                                                                    </li>
                                                                )}
                                                            </ul>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                
                                {fieldErrorMessage('selected_pricing_id') && (
                                    <div className="error-message" style={{ marginTop: '0.5rem', marginLeft: '0.5rem' }}>
                                        <i className="fas fa-exclamation-circle"></i>
                                        <span>{fieldErrorMessage('selected_pricing_id')}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </StepContent>
                );
            // PASO 2: Datos del solicitante
            case 2:
                return (
                    <StepContent
                        title="Datos del solicitante"
                        currentStep={2}
                        totalSteps={3}
                        progress={66}
                        infoTooltip={{
                            title: 'Datos del Solicitante',
                            message: 'Los datos del solicitante son necesarios para realizar el seguimiento de tu solicitud de registro de marca. Esta información permite que el sistema te identifique correctamente y que puedas recibir actualizaciones sobre el estado de tu solicitud. Todos los campos son obligatorios para garantizar una comunicación efectiva durante todo el proceso de registro.'
                        }}
                    >
                        <div className="form-fields">
                            <div className="form-row">
                                <div className="form-field">
                                    <InputField
                                        label="Nombres"
                                        value={formData.applicant_first_name}
                                        onChange={(value) => handleInputChange('applicant_first_name', value)}
                                        onFocus={() => onFocusRemove('applicant_first_name')}
                                        error={fieldErrorMessage('applicant_first_name')}
                                        backgroundColor="#ffffff"
                                        required
                                    />
                                </div>
                                <div className="form-field">
                                    <InputField
                                        label="Apellido paterno"
                                        value={formData.applicant_paternal_last_name}
                                        onChange={(value) => handleInputChange('applicant_paternal_last_name', value)}
                                        onFocus={() => onFocusRemove('applicant_paternal_last_name')}
                                        error={fieldErrorMessage('applicant_paternal_last_name')}
                                        backgroundColor="#ffffff"
                                        required
                                    />
                                </div>
                                <div className="form-field">
                                    <InputField
                                        label="Apellido materno"
                                        value={formData.applicant_maternal_last_name}
                                        onChange={(value) => handleInputChange('applicant_maternal_last_name', value)}
                                        onFocus={() => onFocusRemove('applicant_maternal_last_name')}
                                        error={fieldErrorMessage('applicant_maternal_last_name')}
                                        backgroundColor="#ffffff"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-field">
                                    <InputField
                                        label="Correo"
                                        type="email"
                                        value={formData.applicant_email}
                                        onChange={(value) => handleInputChange('applicant_email', value)}
                                        onFocus={() => onFocusRemove('applicant_email')}
                                        error={fieldErrorMessage('applicant_email')}
                                        backgroundColor="#ffffff"
                                        required
                                    />
                                </div>
                                <div className="form-field">
                                    <PhoneInputField
                                        label="Teléfono"
                                        value={formData.applicant_phone}
                                        onChange={(value) => handleInputChange('applicant_phone', value)}
                                        onFocus={() => onFocusRemove('applicant_phone')}
                                        error={fieldErrorMessage('applicant_phone')}
                                        backgroundColor="#ffffff"
                                        required
                                        defaultCountry="CL"
                                    />
                                </div>
                            </div>
                        </div>
                    </StepContent>
                );
            // PASO 3: Datos de la marca
            case 3:
                return (
                    <StepContent
                        title="Datos de la marca"
                        currentStep={3}
                        totalSteps={3}
                        progress={100}
                    >
                        <div className="form-fields">
                            {/* Selección del tipo de marca */}
                            <div className="form-section">
                                <h3 className="section-title">
                                    Selecciona el tipo de marca que deseas registrar:
                                    <i 
                                        className="fas fa-info-circle ml-2 text-primary"
                                        onClick={() => {
                                            infoAlert({
                                                title: 'Tipos de Marca',
                                                message: 'Existen tres tipos de marca que puedes registrar:\n\n' +
                                                    '• <strong>Marca Mixta</strong>: Combina elementos gráficos (imagen) y texto (nombre). Es la más completa y distintiva.\n\n' +
                                                    '• <strong>Marca Figurativa</strong>: Solo contiene elementos gráficos, símbolos o diseños sin texto. Ideal para logos reconocibles.\n\n' +
                                                    '• <strong>Marca Denominativa</strong>: Solo contiene texto o palabras.\n\n' +
                                                    'La elección del tipo de marca dependerá de tus necesidades y del diseño que desees proteger.',
                                                useHTML: true
                                            });
                                        }}
                                        style={{ cursor: 'pointer' }}
                                        title="Haz clic para más información"
                                    ></i>
                                </h3>
                                <OptionSelector
                                    name="mark_type"
                                    options={[
                                        { value: 'mixta', label: 'Marca mixta' },
                                        { value: 'figurativa', label: 'Marca figurativa' },
                                        { value: 'denominativa', label: 'Marca denominativa' }
                                    ]}
                                    selectedValue={formData.mark_type}
                                    onChange={(value) => {
                                        handleInputChange('mark_type', value);
                                        onFocusRemove('mark_type');
                                    }}
                                    required
                                />
                                {fieldErrorMessage('mark_type') && (
                                    <div className="error-message" style={{ marginTop: '0.5rem', marginLeft: '0.5rem' }}>
                                        <i className="fas fa-exclamation-circle"></i>
                                        <span>{fieldErrorMessage('mark_type')}</span>
                                    </div>
                                )}
                            </div>

                            {/* Campos según el tipo de marca */}
                            {formData.mark_type !== 'figurativa' && (
                                <div className="form-row">
                                    <div className="form-field">
                                        <InputField
                                            label="Nombre de la marca"
                                            value={formData.brand_name || ''}
                                            onChange={(value) => handleInputChange('brand_name', value)}
                                            onFocus={() => onFocusRemove('brand_name')}
                                            error={fieldErrorMessage('brand_name')}
                                            backgroundColor="#ffffff"
                                            required
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="form-row">
                                <div className="form-field full-width">
                                    <TextAreaField
                                        label="Servicios que presta o productos que vende"
                                        value={formData.services_description}
                                        onChange={(value) => handleInputChange('services_description', value)}
                                        onFocus={() => onFocusRemove('services_description')}
                                        error={fieldErrorMessage('services_description')}
                                        placeholder="Detalla con la mayor precisión posible tu empresa, así como los productos o servicios que se presentarán bajo su nombre."
                                        rows={4}
                                        backgroundColor="#ffffff"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Campo de palabras clave solo para marca denominativa */}
                            {formData.mark_type === 'denominativa' && (
                                <div className="form-row">
                                    <div className="form-field">
                                        <InputField
                                            label="Palabras clave"
                                            value={formData.keywords || ''}
                                            onChange={(value) => handleInputChange('keywords', value)}
                                            onFocus={() => onFocusRemove('keywords')}
                                            error={fieldErrorMessage('keywords')}
                                            backgroundColor="#ffffff"
                                            required
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Campo de imagen según el tipo */}
                            {formData.mark_type !== 'denominativa' && (
                                <div className="form-section">
                                    <h3 className="section-title">
                                        Imagen (JPG, JPEG)
                                        <i 
                                            className="fas fa-info-circle ml-2 text-primary"
                                            onClick={() => {
                                                infoAlert({
                                                    title: 'Imagen de la Marca',
                                                    message: 'La imagen de tu marca debe cumplir con los siguientes requisitos:\n\n' +
                                                        '• <strong>Formato</strong>: Solo archivos JPG o JPEG\n' +
                                                        '• <strong>Tamaño máximo</strong>: 2 MB\n' +
                                                        '• <strong>Calidad</strong>: La imagen debe ser clara y de buena resolución\n' +
                                                        '• <strong>Contenido</strong>: Debe mostrar el diseño, logo o símbolo que deseas registrar\n\n' +
                                                        'La imagen será utilizada para el registro oficial ante INAPI. Asegúrate de que sea la versión final y definitiva de tu marca.',
                                                    useHTML: true
                                                });
                                            }}
                                            style={{ cursor: 'pointer' }}
                                            title="Haz clic para más información"
                                        ></i>
                                    </h3>
                                    <FileUpload
                                        onFileSelect={(file) => {
                                            handleInputChange('brand_image', file as File | null);
                                            onFocusRemove('brand_image');
                                        }}
                                        currentFile={formData.brand_image as File | null}
                                        disabled={isSubmitting || fetchingStoreBrandRegistration}
                                        acceptedTypes=".jpg,.jpeg"
                                        maxSize={2}
                                        showPreview={true}
                                        placeholder="Haz click para cargar o arrastra el archivo"
                                    />
                                    {fieldErrorMessage('brand_image') && (
                                        <div className="error-message" style={{ marginTop: '0.5rem', marginLeft: '0.5rem' }}>
                                            <i className="fas fa-exclamation-circle"></i>
                                            <span>{fieldErrorMessage('brand_image')}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </StepContent>
                );
            default:
                return null;
        }
    };

    return (
        <div className="brand-application-container">
            <PageHeader
                breadcrumbs={[
                    { label: 'Inicio', path: '/intranet' },
                    { label: 'Registro de marca', path: '/intranet/brand-registration' },
                    { label: 'Nuevo registro', isCurrent: true }
                ]}
                title="Solicitud de registro de marca"
                description="Completa los datos necesarios para enviar tu solicitud de registro."
            />

            {renderStepContent()}

            <NavigationButtons
                onBack={handleBack}
                onNext={handleNext}
                nextText={currentStep === 3 ? 'Enviar' : 'Siguiente'}
                isSubmitting={isSubmitting}
                disabled={fetchingStoreBrandRegistration || fetchingPlans}
            />
        </div>
    );
};

export default BrandRegistrationApplication;
